import cn from 'classnames'
import { format } from 'date-fns'
import { EltFormOpener } from 'legacy/lut/components/widgets/public/partner360/ELT'
import { Dropdown } from 'legacy/lut/library/dropdown'
import { FC, useCallback, useMemo } from 'react'
import { useSelector } from 'react-redux'

import { InfoBlock, InfoBlockType } from 'cng-common/components/infoBlock'
import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { useFormField } from 'cng-common/features/form'
import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button'
import { Checkbox } from 'core-components/library/checkbox'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'
import { ErrorMessage } from 'partner360/components/QBR/shared/errorMessage/ErrorMessage'
import {
  selectQBRFYear,
  selectQbrLastModifiedDate,
  selectQBRPartners,
} from 'partner360/features/QBR'
import { selectELTVersion } from 'partner360/features/QBR/selectors/selectELTVersion'
import { ISlideSection } from 'partner360/models/qbr/ISlideSection'

import { useQbrSave } from '../../../../../hooks/useQbrSave'
import {
  QBR_SETTINGS_FORM_KEY,
  selectIsQbrChanged,
} from '../../../settingsStep/components/qbrSettingsForm'
import { QBR_EXPORT_TRANSLATIONS_NAMESPACE, TRANSLATION_KEYS } from '../../translations'

import { formatLastModifiedDate } from './helpers/formatLastModifiedDate'
import { getSlidePreviewSrc } from './helpers/getSlidePreviewSrc'
import { useELTVersions } from './hooks/useELTVersions'
import { useFormExportSlides } from './hooks/useFormExportSlides'
import { useQbrDocumentExportActions } from './hooks/useQbrDocumentExportActions'
import { IELTVersions } from './models/IELTVersions'

import styles from './QbrExport.module.scss'

export const QbrExport: FC = () => {
  /** -------------------- Selectors ----------------------- */

  const initialELTVersion = useSelector(selectELTVersion)
  const isQbrChanged = useSelector(selectIsQbrChanged)
  const isQbrYear = useSelector(selectQBRFYear)
  const lastModified = useSelector(selectQbrLastModifiedDate)
  const qbrPartners = useSelector(selectQBRPartners)

  /** -------------------- Hooks ----------------------- */

  const { exportSlides, onChangeExportSlide, isExportSlidesModified } = useFormExportSlides()
  const { checkAndSave } = useQbrSave()
  const { isLoading, errorMessage, clearResponseMessage, generateAndDownloadQbr } =
    useQbrDocumentExportActions()
  const { isLoading: isEltLoading, eltVersions, refresh: refreshEltVersions } = useELTVersions()
  const {
    value: currentELTVersion,
    handleChange: handleChangeELTVersion,
    isModified: isELTVersionModified,
  } = useFormField({
    defaultValue: initialELTVersion,
    field: 'eltVersion',
    formKey: QBR_SETTINGS_FORM_KEY,
  })

  /** -------------------- Variables ----------------------- */

  const eltVersionsSource = useMemo(() => {
    const staticCrud = new Crud<IELTVersions>({
      fetcher: new Static(eltVersions),
      keyField: 'UID',
      defaultPaging: { page: 0, pageSize: 15 },
      defaultSort: { field: 'Version', dir: 'desc' },
    })
    staticCrud.removeQueries(true)

    return staticCrud
  }, [eltVersions])

  const qbrPartnerId = useMemo(() => {
    return qbrPartners.length ? qbrPartners[0].PartnerID : ''
  }, [qbrPartners])

  /** -------------------- User actions handlers ----------------------- */

  const handleChangeSelectedELTVersion = useCallback(
    async (value: string, item?: IELTVersions) => {
      await handleChangeELTVersion(item?.UID ?? '')
    },
    [handleChangeELTVersion]
  )

  const handleChangeSelectedSlides = useCallback(
    async (slideSection: ISlideSection, isSelected: boolean) => {
      const newSlideSection: ISlideSection = { ...slideSection, IsSelected: isSelected ? 1 : 0 }

      await onChangeExportSlide(newSlideSection)
    },
    [onChangeExportSlide]
  )

  const handleCheckSaveAndGenerate = useCallback(async () => {
    if (isQbrChanged || isExportSlidesModified) {
      const isSavedSuccessful = await checkAndSave({
        shouldShowConfirmationDialog: true,
        shouldKeepCurrentStep: true,
      })
      if (!isSavedSuccessful) {
        return
      }
    }

    await generateAndDownloadQbr()
  }, [checkAndSave, generateAndDownloadQbr, isExportSlidesModified, isQbrChanged])

  const handleRefreshELTVersions = useCallback(async () => {
    await refreshEltVersions()
  }, [refreshEltVersions])

  /** -------------------- Render ----------------------- */

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading || isEltLoading} />

      <div className={styles.clarificationMessage}>
        <Translation
          htmlAvailable
          text={TRANSLATION_KEYS.qbrExport.qbrLastChangesMessage}
          replaceParams={[
            {
              value: formatLastModifiedDate(lastModified),
              key: '{lastModified}',
            },
          ]}
        />
      </div>

      <div className={cn(styles.clarificationMessage, styles.subtitle)}>
        <Translation htmlAvailable text={TRANSLATION_KEYS.qbrExport.pleaseGenerateQbr} />
      </div>

      <div className={styles.slidesContainer}>
        {exportSlides
          .filter((el) => el.Type === 'qbr')
          .map((item, index) => {
            return (
              <div
                className={cn(styles.slideItem, { [styles.selectedItem]: item.IsSelected })}
                key={`qbr-preview-${index}`}
              >
                <div className={styles.slidePreview}>
                  <img
                    className={styles.slideImage}
                    src={getSlidePreviewSrc('qbr', index)}
                    alt={`QBR Slide ${index + 1}`}
                  />
                </div>
                <div className={styles.slideSelector}>
                  <Checkbox
                    checked={!!item.IsSelected}
                    onChange={(isSelected) => handleChangeSelectedSlides(item, isSelected)}
                    label={
                      <Translation text={`${QBR_EXPORT_TRANSLATIONS_NAMESPACE}--${item.Key}`} />
                    }
                    margin={'md'}
                  />
                </div>
              </div>
            )
          })}
      </div>

      {!!qbrPartnerId && (
        <>
          <div className={cn(styles.clarificationMessage, styles.subtitle)}>
            <Translation htmlAvailable text={TRANSLATION_KEYS.qbrExport.selectEltSlidesMessage} />
          </div>

          <div className={styles.eltDisclaimer}>
            <InfoBlock
              text={TRANSLATION_KEYS.qbrExport.eltSlidesDisclaimer}
              type={InfoBlockType.Warning}
            />
          </div>

          <div className={styles.eltContainer}>
            <div className={styles.eltDropdownContainer}>
              <Dropdown
                classes={{
                  controlClasses: {
                    root: styles.eltDropdown,
                  },
                  inputClasses: {
                    root: cn({ [styles.modified]: isELTVersionModified }),
                  },
                }}
                value={currentELTVersion}
                source={eltVersionsSource}
                label="Select ELT Version"
                caption="VersionName"
                renderFunction={(item) => (
                  <div>
                    <div className={styles.eltDropdownItemTitle}>{item.VersionName}</div>
                    <div>{item.ModifiedBy}</div>
                    <span>{format(new Date(item.Modified), 'dd MMM yyyy HH:mm')}</span>
                  </div>
                )}
                margin={'md'}
                onChange={handleChangeSelectedELTVersion}
              />
              <Button
                className={styles.eltRefreshButton}
                title="Refresh ELT Versions"
                iconButton={'kbase kbase-reload kbase-xs'}
                variant={'text'}
                onClick={handleRefreshELTVersions}
              />
              <div className={styles.createEltLabel}>
                <Translation text={TRANSLATION_KEYS.qbrExport.createNewEltMessage} span />
              </div>
            </div>
            <EltFormOpener partnerId={qbrPartnerId} fYear={isQbrYear} />
          </div>

          {!!currentELTVersion && (
            <div className={styles.slidesContainer}>
              {exportSlides
                .filter((el) => el.Type === 'elt')
                .map((item, index) => {
                  return (
                    <div
                      className={cn(styles.slideItem, { [styles.selectedItem]: item.IsSelected })}
                      key={`elt-preview-${index}`}
                    >
                      <div className={styles.slidePreview}>
                        <img
                          className={styles.slideImage}
                          src={getSlidePreviewSrc('elt', index)}
                          alt={`ELT Slide ${index + 1}`}
                        />
                      </div>
                      <div className={styles.slideSelector}>
                        <Checkbox
                          checked={!!item.IsSelected}
                          onChange={(isSelected) => handleChangeSelectedSlides(item, isSelected)}
                          label={
                            <Translation
                              text={`${QBR_EXPORT_TRANSLATIONS_NAMESPACE}--${item.Key}`}
                            />
                          }
                          margin={'md'}
                        />
                      </div>
                    </div>
                  )
                })}
            </div>
          )}
        </>
      )}

      <div className={styles.actionButtons}>
        <Button
          color="primary"
          className={styles.downloadQbrButton}
          onClick={handleCheckSaveAndGenerate}
        >
          <Translation htmlAvailable text={TRANSLATION_KEYS.qbrExport.downloadQbrButton} />
        </Button>
      </div>
      {errorMessage && <ErrorMessage message={errorMessage} onClose={clearResponseMessage} />}
    </div>
  )
}
